import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Flex, Group, Menu, Text, UnstyledButton } from '@mantine/core';

import { useGetUser } from 'api/account';
import { UserAvatar } from 'components/shared';
import { ContextMenuIcon, EditIcon, ShieldIcon, TrashIcon } from 'icons';
import type { BoardUserType } from 'types';

import { MemberAdminModal } from './MemberAdminModal';
import { MemberDeleteModal } from './MemberDeleteModal';
import { MemberPermissionsModal } from './MemberPermissionsModal';

type Props = {
  user: BoardUserType;
  isAdministrator: boolean;
};

export const MemberRow = ({ user, isAdministrator }: Props) => {
  const { t } = useTranslation('organisations');

  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [permissionsModalOpened, setPermissionsModalOpened] = useState(false);
  const [adminModalOpened, setAdminModalOpened] = useState(false);

  const { data: currentUser } = useGetUser();
  const currentUserId = currentUser?.id;

  return (
    <tr key={user.id}>
      <td>
        <Group spacing="sm">
          <UserAvatar
            name={user.name}
            surname={user.surname}
            avatar={user.avatarUrl}
          />
          <Text>
            {user.name} {user.surname}
          </Text>
        </Group>
      </td>
      <td>
        <Text fz={13} color="gray.5">
          {user.email}
        </Text>
      </td>
      <td>
        {user.isAdminOrg && (
          <Badge color="blue" variant="outline">
            {t('organisationView.boards.members.list.item.adminLabel')}
          </Badge>
        )}
      </td>

      <td>
        {isAdministrator && (
          <Flex justify="flex-end" gap="md">
            <UnstyledButton onClick={() => setPermissionsModalOpened(true)}>
              <EditIcon />
            </UnstyledButton>
            {currentUserId !== user.id && (
              <Menu position="bottom-end">
                <Menu.Target>
                  <UnstyledButton>
                    <ContextMenuIcon />
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => setAdminModalOpened(true)}>
                    <Flex gap="sm" align="center">
                      <ShieldIcon />
                      <Text>
                        {t(
                          `organisationView.boards.members.list.item.actions.${
                            user.isAdminOrg ? 'revoke' : 'grant'
                          }Admin`,
                        )}
                      </Text>
                    </Flex>
                  </Menu.Item>
                  <Menu.Item onClick={() => setDeleteModalOpened(true)}>
                    <Flex gap="sm" align="center">
                      <TrashIcon color="red" />
                      <Text c="red">
                        {t(
                          'organisationView.boards.members.list.item.actions.delete',
                        )}
                      </Text>
                    </Flex>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Flex>
        )}
        <MemberPermissionsModal
          opened={permissionsModalOpened}
          onClose={() => setPermissionsModalOpened(false)}
          user={user}
        />
        <MemberAdminModal
          opened={adminModalOpened}
          onClose={() => setAdminModalOpened(false)}
          user={user}
        />
        <MemberDeleteModal
          opened={deleteModalOpened}
          onClose={() => setDeleteModalOpened(false)}
          user={user}
        />
      </td>
    </tr>
  );
};
